import { useWithNumberId, WithNumberId, withNumberId } from 'hooks/useId';
import { FC, useMemo } from 'react';
import * as S from '../profile/Profile.styled';
import {
    deleteCourseAssignments,
    getCourseAssignments,
    getCourseById
} from 'api/courses/courses';
import { HeaderDataRow } from 'pages/profile/DataRow';
import { GrayTextM, TextL } from 'ui/Styled/Styled';
import { Skeleton } from 'components/Skeleton';
import { getTranslated } from 'messages';
import { withContainer } from 'components/Container';
import { withAuth } from 'context/Auth';
import { getCommitteeById } from 'api/committees/committees';
import { DocumentType, OrganizationType } from 'api/api';
import styled from 'styled-components';
import { RouterPaths } from 'helpers/router-paths';
import { useQuery } from '@tanstack/react-query';
import { NotFoundPageContent } from 'pages/errors/404';
import { EditIcon } from 'assets/Edit';
import { StandartLink } from 'ui/Link';
import { QueryKey } from 'helpers/constants';
import { PowerTable } from 'ui/PowerTable';
import { ColumnProps } from 'ui/Table';
import { useOrganizationType } from 'context/User';
import { ReactComponent as LockIcon } from 'assets/lock-alt.svg';
import { ReactComponent as UnlockIcon } from 'assets/unlock-alt.svg';
import { AssignedCourse } from 'api/courses/courses.types';
import {
    ASSIGNED_STUDENTS_DELETE_CORE_WORD,
    ASSIGNED_STUDENTS_DELETE_WORDS
} from './constants';

const DataSection = styled.div`
    margin-top: 6px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    gap: 10px;

    & > div {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
`;

const CourseTitle = styled(TextL)`
    max-width: 30em;
`;

const CourseHeader = styled.div`
    display: flex;
    flex-flow: row;
    justify-content: space-between;
    width: 100%;
    gap: 6px;
`;

const CourseEditButton = styled(StandartLink)`
    padding: 5px;
    cursor: pointer;
    margin-left: 8px;
    vertical-align: middle;
    display: inline-block;
`;

const CourseLockedText = styled(GrayTextM)`
    & svg {
        vertical-align: middle;
        margin-left: 5px;
    }
`;

const CourseHeaderActions = styled.div`
    display: flex;
    flex-flow: row;
    gap: 20px;
    margin-left: auto;
`;

const ASSIGNED_STUDENTS_COLUMNS: ColumnProps<AssignedCourse> = {
    'student.avatar': { title: '', width: 60 },
    'student.user.fullName': 'ФИО',
    'student.user.phone': 'Номер телефона',
    'student.birthday': {
        title: 'Дата рождения',
        type: 'date'
    }
};

const SingleCourse: FC<WithNumberId> = ({ id }) => {
    const { data: course, error } = useQuery({
        queryKey: [QueryKey.Course, id],
        queryFn: () => getCourseById(id)
    });

    const { data: committee } = useQuery({
        queryKey: [QueryKey.Committee, course?.committeeId],
        queryFn: () =>
            course?.committeeId ? getCommitteeById(course.committeeId) : null,
        enabled: !!course?.committeeId
    });

    const getter = useWithNumberId(getCourseAssignments);

    const disciplines = useMemo(() => {
        if (!course?.disciplines || !course.disciplines.length) {
            return null;
        }

        return course.disciplines.map((d) => d.title).join(', ');
    }, [course]);

    const issuedDocuments = useMemo(() => {
        if (!course?.issuedDocuments || !course.issuedDocuments.length) {
            return null;
        }

        return course.issuedDocuments
            .map(
                (doc) =>
                    getTranslated('documentTypes', doc as DocumentType) || doc
            )
            .join(', ');
    }, [course]);

    const organizationType = useOrganizationType();

    if (error) {
        return (
            <>
                <NotFoundPageContent />
            </>
        );
    }

    if (!course) {
        return <Skeleton />;
    }

    return (
        <>
            <S.ContainerTopContent>
                <S.ContainerMainInformation>
                    <CourseHeader>
                        <CourseTitle>
                            {course.title}
                            {organizationType ===
                                OrganizationType.EducationCenter && (
                                <CourseEditButton
                                    to={RouterPaths.CourseEdit(id)}
                                >
                                    <EditIcon />
                                </CourseEditButton>
                            )}
                        </CourseTitle>
                        <CourseHeaderActions>
                            <CourseLockedText>
                                Курс{' '}
                                {course.isLocked ? (
                                    <>закрыт {<LockIcon />}</>
                                ) : (
                                    <>открыт {<UnlockIcon />}</>
                                )}
                            </CourseLockedText>
                        </CourseHeaderActions>
                    </CourseHeader>
                    <DataSection>
                        <div>
                            <HeaderDataRow
                                label="Вид обучения"
                                value={
                                    course.trainingType
                                        ? getTranslated(
                                              'trainingTypes',
                                              course.trainingType
                                          )
                                        : null
                                }
                            />

                            <HeaderDataRow
                                label="Присваиваемая квалификация"
                                value={course.profession?.title}
                            />

                            <HeaderDataRow
                                label="Перечень дисциплин"
                                value={disciplines}
                            />

                            <HeaderDataRow
                                label="Количество часов"
                                value={course.hoursCount ?? '—'}
                            />
                        </div>

                        <div>
                            <HeaderDataRow
                                label="Выдаваемый документ"
                                value={issuedDocuments}
                            />

                            <HeaderDataRow
                                label="Список комиссий"
                                value={committee?.title}
                            />

                            <HeaderDataRow
                                label="Срок действия документа (кол-во лет)"
                                value={course.validityPeriodDocument ?? '—'}
                            />

                            <HeaderDataRow
                                label="Стоимость обучения"
                                value={course.price && `${course.price} ₽`}
                            />

                            <HeaderDataRow
                                label="Доступен в Moodle"
                                value={course.moodleId ? 'Да' : 'Нет'}
                            />
                        </div>
                    </DataSection>
                </S.ContainerMainInformation>
            </S.ContainerTopContent>
            <PowerTable
                columns={ASSIGNED_STUDENTS_COLUMNS}
                getter={getter}
                noRowsMsg="Студенты не найдены"
                onDelete={deleteCourseAssignments}
                deleteWords={ASSIGNED_STUDENTS_DELETE_WORDS}
                deleteCoreWord={ASSIGNED_STUDENTS_DELETE_CORE_WORD}
            />
        </>
    );
};

export default withContainer(withAuth(withNumberId(SingleCourse)), {
    additionalItem: {
        text: 'Просмотр курса',
        path: ''
    },
    parentPath: RouterPaths.FindCourse,
    flex: true,
    disablePaddingTop: true
});
